/**
 * Subscription Context
 * Provides subscription status and feature gating throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

export const SubscriptionContext = createContext(null);

// Paddle.js loader
const loadPaddleJS = () => {
  return new Promise((resolve, reject) => {
    if (window.Paddle) {
      resolve(window.Paddle);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => resolve(window.Paddle);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export function SubscriptionProvider({ children, backendService, isSignedIn }) {
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [paddleReady, setPaddleReady] = useState(false);
  const [prices, setPrices] = useState([]);
  const [gatingEnabled, setGatingEnabled] = useState(false);

  // Check if gating is enabled on the backend
  useEffect(() => {
    const checkConfig = async () => {
      if (!backendService) return;
      try {
        const config = await backendService.getSubscriptionConfig();
        setGatingEnabled(config.gatingEnabled);
        console.log('[Subscription] Gating enabled:', config.gatingEnabled);
      } catch (error) {
        console.error('[Subscription] Failed to fetch config:', error);
        setGatingEnabled(false);
      }
    };
    checkConfig();
  }, [backendService]);

  const refreshSubscriptionRef = useRef(null);

  // Initialize Paddle
  useEffect(() => {
    const initPaddle = async () => {
      try {
        const Paddle = await loadPaddleJS();
        const environment = process.env.REACT_APP_PADDLE_ENVIRONMENT || 'sandbox';
        const token = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
        
        if (token) {
          Paddle.Initialize({
            token,
            environment,
            eventCallback: (event) => {
              console.log('[Paddle] Event:', event.name, event.data);
              
              // Refresh subscription after successful checkout
              if (event.name === 'checkout.completed') {
                setTimeout(() => {
                  refreshSubscriptionRef.current?.();
                }, 2000); // Give webhook time to process
              }
            }
          });
          setPaddleReady(true);
          console.log('[Subscription] Paddle initialized');
        } else {
          console.warn('[Subscription] No Paddle client token configured');
        }
      } catch (error) {
        console.error('[Subscription] Failed to load Paddle:', error);
      }
    };

    initPaddle();
  }, []);

  // Fetch subscription status
  const refreshSubscription = useCallback(async () => {
    if (!isSignedIn || !backendService) {
      setSubscription({ tier: 'free', status: 'none', features: {} });
      setFeatures({});
      setLoading(false);
      return;
    }

    try {
      const status = await backendService.getSubscriptionStatus();
      setSubscription(status);
      setFeatures(status.features || {});
    } catch (error) {
      console.error('[Subscription] Failed to fetch status:', error);
      setSubscription({ tier: 'free', status: 'none', features: {} });
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, backendService]);

  useEffect(() => {
    refreshSubscriptionRef.current = refreshSubscription;
  }, [refreshSubscription]);

  // Fetch prices
  const fetchPrices = useCallback(async () => {
    if (!backendService) return;
    
    try {
      const data = await backendService.getSubscriptionPrices();
      setPrices(data.prices || []);
    } catch (error) {
      console.error('[Subscription] Failed to fetch prices:', error);
    }
  }, [backendService]);

  // Load subscription on auth change
  useEffect(() => {
    refreshSubscription();
    fetchPrices();
  }, [refreshSubscription, fetchPrices]);

  // Check if user has access to a feature
  const hasFeature = useCallback((feature) => {
    // If gating is disabled, all features are available
    if (!gatingEnabled) return true;
    return features[feature] === true;
  }, [features, gatingEnabled]);

  // Check if user is on Pro tier
  const isPro = useCallback(() => {
    // If gating is disabled, everyone is effectively "pro"
    if (!gatingEnabled) return true;
    return subscription?.tier === 'pro';
  }, [subscription, gatingEnabled]);

  // Open Paddle checkout
  const openCheckout = useCallback(async (priceId) => {
    if (!paddleReady || !window.Paddle) {
      console.error('[Subscription] Paddle not ready');
      return;
    }

    try {
      const checkoutData = await backendService.createCheckout(priceId);
      
      window.Paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customer: checkoutData.customer_id ? { id: checkoutData.customer_id } : undefined,
        customData: checkoutData.custom_data,
        settings: {
          displayMode: 'overlay',
          theme: 'dark',
          locale: 'en',
          successUrl: window.location.origin + '?checkout=success',
          allowLogout: false
        }
      });
    } catch (error) {
      console.error('[Subscription] Failed to open checkout:', error);
      throw error;
    }
  }, [paddleReady, backendService]);

  // Open customer portal
  const openPortal = useCallback(async () => {
    try {
      const { portal_url } = await backendService.getSubscriptionPortal();
      window.open(portal_url, '_blank');
    } catch (error) {
      console.error('[Subscription] Failed to open portal:', error);
      throw error;
    }
  }, [backendService]);

  const value = {
    subscription,
    features,
    loading,
    isPro,
    hasFeature,
    prices,
    paddleReady,
    gatingEnabled,
    openCheckout,
    openPortal,
    refreshSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    // Return safe defaults when used outside provider (e.g., in modals)
    // This allows components to render without crashing
    return {
      subscription: null,
      features: {},
      loading: false,
      isPro: () => true, // Default to unlocked when no context
      hasFeature: () => true, // Default to unlocked when no context
      prices: [],
      paddleReady: false,
      gatingEnabled: false,
      openCheckout: () => console.warn('Subscription context not available'),
      openPortal: () => console.warn('Subscription context not available'),
      refreshSubscription: () => Promise.resolve()
    };
  }
  return context;
}

/**
 * Hook to check if a specific feature is available
 * Returns { hasAccess, loading, openUpgrade }
 */
export function useFeature(feature) {
  const { hasFeature, loading, openCheckout, prices } = useSubscription();
  
  const hasAccess = hasFeature(feature);
  
  const openUpgrade = useCallback(() => {
    // Default to monthly price
    const monthlyPrice = prices.find(p => p.interval === 'monthly');
    if (monthlyPrice?.id) {
      openCheckout(monthlyPrice.id);
    }
  }, [prices, openCheckout]);
  
  return { hasAccess, loading, openUpgrade };
}

export default SubscriptionContext;
